// ----------------------------------------------------------------------
// The Beacon keyboard shortcut (epic #617 P3): `/` or ⌘K / Ctrl+K opens the drawer from any
// page with the cursor in the question box.

type KeyLike = Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'defaultPrevented' | 'isComposing'>;

/** What the key landed on — just enough of the target to tell whether it's a text field. */
export type TargetLike = {
  tagName?: string;
  isContentEditable?: boolean;
  getAttribute?: (name: string) => string | null;
  closest?: (selector: string) => unknown;
} | null;

function isTextField(t: TargetLike): boolean {
  if (!t) return false;
  const tag = (t.tagName ?? '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return t.getAttribute?.('role') === 'textbox';
}

function isRichEditor(t: TargetLike): boolean {
  return !!t?.isContentEditable;
}

/** Inside an open modal (a dialog, or Beacon's own drawer) the shortcut belongs to it. */
function inModal(t: TargetLike): boolean {
  return !!t?.closest?.('[role="dialog"], .MuiModal-root');
}

/**
 * Whether a keydown should open Beacon.
 * - `/` (no modifiers) only when focus is not in a text field, a rich editor or a modal —
 *   so typing a fraction never opens it.
 * - ⌘K / Ctrl+K also from a plain text field, but never in a rich editor (tiptap uses ⌘K for
 *   links) or a modal.
 */
export function isBeaconShortcut(e: KeyLike, target: TargetLike): boolean {
  if (e.defaultPrevented || e.isComposing) return false;
  if (inModal(target) || isRichEditor(target)) return false;
  if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) return !isTextField(target);
  if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') return true;
  return false;
}

/** "⌘K" on a Mac, else "Ctrl K" — the hint on the button and in the drawer. */
export function shortcutHint(platform: string = typeof navigator === 'undefined' ? '' : navigator.platform): string {
  return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl K';
}
